<script setup>
import { computed, ref, onMounted } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const activeTab = ref('all');
const isLoading = ref(false);
const showLightbox = ref(false);
const selectedMediaIndex = ref(0);

useI18n();

const conversationGetter = useMapGetter('getConversationById');
const conversation = computed(() =>
  conversationGetter.value(props.conversationId)
);

const messages = computed(() => {
  const conv = conversation.value;
  if (!conv) return [];

  // Get messages from conversation payload
  const conversationMessages = conv.messages || [];

  // Ensure it's an array
  if (!Array.isArray(conversationMessages)) {
    return [];
  }

  return conversationMessages;
});

const filteredMedia = computed(() => {
  const msgArray = messages.value;

  // Ensure messages is an array before filtering
  if (!Array.isArray(msgArray)) {
    return [];
  }

  const media = msgArray
    .filter(msg => msg && msg.attachments && msg.attachments.length > 0)
    .flatMap(msg =>
      msg.attachments.map(attachment => ({
        ...attachment,
        messageId: msg.id,
        timestamp: msg.created_at,
      }))
    )
    // Sort by timestamp descending (most recent first)
    .sort((a, b) => {
      const dateA = new Date(a.timestamp).getTime();
      const dateB = new Date(b.timestamp).getTime();
      return dateB - dateA;
    });

  if (activeTab.value === 'all') return media;
  if (activeTab.value === 'photos') {
    return media.filter(item => {
      const type = item.file_type?.toLowerCase() || '';
      return type.includes('image');
    });
  }
  if (activeTab.value === 'videos') {
    return media.filter(item => {
      const type = item.file_type?.toLowerCase() || '';
      return type.includes('video');
    });
  }
  if (activeTab.value === 'audio') {
    return media.filter(item => {
      const type = item.file_type?.toLowerCase() || '';
      return type.includes('audio');
    });
  }
  if (activeTab.value === 'documents') {
    return media.filter(item => {
      const type = item.file_type?.toLowerCase() || '';
      return (
        type.includes('pdf') ||
        type.includes('word') ||
        type.includes('document') ||
        type.includes('sheet') ||
        type.includes('excel') ||
        type.includes('zip') ||
        type.includes('rar') ||
        type.includes('compressed')
      );
    });
  }
  return media;
});

const getMediaIcon = fileType => {
  const type = fileType?.toLowerCase() || '';
  if (type.includes('image')) return 'i-lucide-image';
  if (type.includes('video')) return 'i-lucide-video';
  if (type.includes('audio')) return 'i-lucide-music';
  if (type.includes('pdf')) return 'i-lucide-file-pdf';
  if (type.includes('word') || type.includes('document'))
    return 'i-lucide-file-text';
  if (type.includes('sheet') || type.includes('excel')) return 'i-lucide-table';
  return 'i-lucide-file';
};

const getMediaThumbnail = item => {
  const type = item.file_type?.toLowerCase() || '';
  if (type.includes('image')) {
    return item.thumb_url || item.data_url || item.url;
  }
  return null;
};

const openMedia = item => {
  const index = filteredMedia.value.findIndex(m => m.id === item.id);
  if (index !== -1) {
    selectedMediaIndex.value = index;
    showLightbox.value = true;
  }
};

const closeLightbox = () => {
  showLightbox.value = false;
};

const nextMedia = () => {
  if (selectedMediaIndex.value < filteredMedia.value.length - 1) {
    selectedMediaIndex.value += 1;
  }
};

const prevMedia = () => {
  if (selectedMediaIndex.value > 0) {
    selectedMediaIndex.value -= 1;
  }
};

const currentMedia = computed(() => {
  return filteredMedia.value[selectedMediaIndex.value];
});

onMounted(() => {
  isLoading.value = false;
});
</script>

<template>
  <div class="flex flex-col h-full w-full">
    <!-- Tabs -->
    <div class="flex gap-2 px-4 py-3 border-b border-n-weak overflow-x-auto">
      <button
        class="px-3 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-colors"
        :class="[
          activeTab === 'all'
            ? 'bg-n-brand text-white'
            : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2',
        ]"
        @click="activeTab = 'all'"
      >
        {{ $t('CONVERSATION_SIDEBAR.MEDIA_GALLERY.ALL') }}
      </button>
      <button
        class="px-3 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-colors"
        :class="[
          activeTab === 'photos'
            ? 'bg-n-brand text-white'
            : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2',
        ]"
        @click="activeTab = 'photos'"
      >
        {{ $t('CONVERSATION_SIDEBAR.MEDIA_GALLERY.PHOTOS') }}
      </button>
      <button
        class="px-3 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-colors"
        :class="[
          activeTab === 'videos'
            ? 'bg-n-brand text-white'
            : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2',
        ]"
        @click="activeTab = 'videos'"
      >
        {{ $t('CONVERSATION_SIDEBAR.MEDIA_GALLERY.VIDEOS') }}
      </button>
      <button
        class="px-3 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-colors"
        :class="[
          activeTab === 'audio'
            ? 'bg-n-brand text-white'
            : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2',
        ]"
        @click="activeTab = 'audio'"
      >
        {{ $t('CONVERSATION_SIDEBAR.MEDIA_GALLERY.AUDIO') }}
      </button>
      <button
        class="px-3 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-colors"
        :class="[
          activeTab === 'documents'
            ? 'bg-n-brand text-white'
            : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2',
        ]"
        @click="activeTab = 'documents'"
      >
        {{ $t('CONVERSATION_SIDEBAR.MEDIA_GALLERY.DOCUMENTS') }}
      </button>
    </div>

    <!-- Media Grid -->
    <div class="flex-1 overflow-y-auto px-4 py-3">
      <div v-if="isLoading" class="flex items-center justify-center h-32">
        <div class="text-n-slate-10">
          {{ $t('CONVERSATION_SIDEBAR.MEDIA_GALLERY.LOADING') }}
        </div>
      </div>
      <div
        v-else-if="filteredMedia.length === 0"
        class="flex flex-col items-center justify-center h-32 text-center"
      >
        <Icon icon="i-lucide-inbox" class="w-8 h-8 text-n-slate-9 mb-2" />
        <p class="text-sm text-n-slate-10">
          {{ $t('CONVERSATION_SIDEBAR.MEDIA_GALLERY.NO_MEDIA') }}
        </p>
      </div>
      <div v-else class="grid grid-cols-3 gap-2">
        <div
          v-for="item in filteredMedia"
          :key="`${item.messageId}-${item.id}`"
          class="relative group cursor-pointer bg-n-alpha-1 rounded-lg overflow-hidden aspect-square hover:bg-n-alpha-2 transition-colors"
          @click="openMedia(item)"
        >
          <!-- Image Thumbnail -->
          <img
            v-if="getMediaThumbnail(item)"
            :src="getMediaThumbnail(item)"
            :alt="item.filename"
            class="w-full h-full object-cover"
          />
          <!-- Video Thumbnail with Play Icon -->
          <div
            v-else-if="item.file_type?.toLowerCase().includes('video')"
            class="w-full h-full bg-gradient-to-br from-gray-800 to-black flex items-center justify-center relative"
          >
            <Icon
              icon="i-lucide-play-circle"
              class="w-12 h-12 text-white opacity-80"
            />
          </div>
          <!-- Audio Icon -->
          <div
            v-else-if="item.file_type?.toLowerCase().includes('audio')"
            class="w-full h-full bg-gradient-to-br from-purple-800 to-purple-900 flex items-center justify-center"
          >
            <Icon icon="i-lucide-music" class="w-8 h-8 text-white" />
          </div>
          <!-- Document Icon -->
          <div
            v-else
            class="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-700 to-gray-900"
          >
            <Icon
              :icon="getMediaIcon(item.file_type)"
              class="w-8 h-8 text-white"
            />
          </div>

          <!-- Overlay -->
          <div
            class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center"
          >
            <Icon
              icon="i-lucide-download"
              class="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity"
            />
          </div>

          <!-- Video Duration Badge -->
          <div
            v-if="item.file_type?.toLowerCase().includes('video')"
            class="absolute bottom-1 right-1 bg-black bg-opacity-70 text-white text-xs px-1.5 py-0.5 rounded"
          >
            {{ item.duration || '0:00' }}
          </div>

          <!-- File Name Badge for Documents -->
          <div
            v-if="
              item.file_type?.toLowerCase().includes('pdf') ||
              item.file_type?.toLowerCase().includes('word') ||
              item.file_type?.toLowerCase().includes('sheet')
            "
            class="absolute bottom-1 left-1 right-1 bg-black bg-opacity-70 text-white text-xs px-1.5 py-0.5 rounded truncate"
            :title="item.filename"
          >
            {{ item.filename }}
          </div>
        </div>
      </div>
    </div>

    <!-- Lightbox Modal -->
    <div
      v-if="showLightbox && currentMedia"
      class="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center"
      @click="closeLightbox"
    >
      <!-- Close Button -->
      <button
        class="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
        @click.stop="closeLightbox"
      >
        <Icon icon="i-lucide-x" class="w-8 h-8" />
      </button>

      <!-- Image Container -->
      <div
        class="relative max-w-4xl max-h-[90vh] flex items-center justify-center"
        @click.stop
      >
        <!-- Image -->
        <img
          v-if="currentMedia.file_type?.toLowerCase().includes('image')"
          :src="currentMedia.data_url || currentMedia.url"
          :alt="currentMedia.filename"
          class="max-w-full max-h-[90vh] object-contain"
        />
        <!-- Video -->
        <video
          v-else-if="currentMedia.file_type?.toLowerCase().includes('video')"
          :src="currentMedia.data_url || currentMedia.url"
          controls
          class="max-w-full max-h-[90vh] object-contain"
        />
        <!-- Audio -->
        <div
          v-else-if="currentMedia.file_type?.toLowerCase().includes('audio')"
          class="text-white text-center flex flex-col items-center justify-center gap-4"
        >
          <Icon icon="i-lucide-music" class="w-16 h-16 text-purple-400" />
          <div class="max-w-xs">
            <p class="font-semibold break-words">{{ currentMedia.filename }}</p>
          </div>
          <audio
            :src="currentMedia.data_url || currentMedia.url"
            controls
            class="w-full max-w-xs"
          />
        </div>
        <!-- Document -->
        <div
          v-else
          class="text-white text-center flex flex-col items-center justify-center gap-4"
        >
          <Icon
            :icon="getMediaIcon(currentMedia.file_type)"
            class="w-16 h-16"
          />
          <div class="max-w-xs">
            <p class="font-semibold break-words">{{ currentMedia.filename }}</p>
            <p class="text-sm text-gray-400 mt-2">
              {{ (currentMedia.file_size / 1024 / 1024).toFixed(2) }}
              {{ $t('CONVERSATION_SIDEBAR.MEDIA_GALLERY.DOWNLOAD_MB') }}
            </p>
          </div>
          <a
            :href="currentMedia.data_url"
            target="_blank"
            rel="noopener noreferrer"
            class="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium transition-colors"
          >
            {{ $t('CONVERSATION_SIDEBAR.MEDIA_GALLERY.DOWNLOAD') }}
          </a>
        </div>

        <!-- Navigation Buttons -->
        <button
          v-if="selectedMediaIndex > 0"
          class="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors"
          @click.stop="prevMedia"
        >
          <Icon icon="i-lucide-chevron-left" class="w-8 h-8" />
        </button>
        <button
          v-if="selectedMediaIndex < filteredMedia.length - 1"
          class="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors"
          @click.stop="nextMedia"
        >
          <Icon icon="i-lucide-chevron-right" class="w-8 h-8" />
        </button>

        <!-- Counter -->
        <div
          class="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black bg-opacity-50 px-3 py-1 rounded"
        >
          {{ selectedMediaIndex + 1 }} / {{ filteredMedia.length }}
        </div>
      </div>
    </div>
  </div>
</template>
